import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import consultantApi from '../../services/consultantApi';
import '../Employer/Employer.css';

const ConsultantDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalJobs: 0,
    activeJobs: 0,
    draftJobs: 0,
    totalApplications: 0,
    pendingApplications: 0
  });
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await consultantApi.getDashboardStats();
      // Backend returns nested data structure: response.data.data
      const data = response.data.data || response.data;
      if (data) {
        setStats({
          totalCompanies: data.companies?.total || 0,
          activeCompanies: data.companies?.active || 0,
          totalJobs: data.jobs?.total || 0,
          activeJobs: data.jobs?.published || 0,
          draftJobs: data.jobs?.draft || 0,
          totalApplications: data.applications?.total || 0,
          pendingApplications: data.applications?.pending || 0
        });
        if (data.recentCompanies) setRecentCompanies(data.recentCompanies);
        if (data.recentJobs) setRecentJobs(data.recentJobs);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active:   { color: 'success', icon: 'check_circle', label: 'Active' },
      pending:  { color: 'warning', icon: 'schedule',     label: 'Pending' },
      draft:    { color: 'warning', icon: 'edit',         label: 'Draft' },
      inactive: { color: 'danger',  icon: 'cancel',       label: 'Inactive' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`dash-status-badge ${config.color}`}>
        <span className="material-symbols-outlined">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="consultant-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="consultant-dashboard">
      {/* Header */}
      <div className="dash-header">
        <div className="dash-header-left">
          <p className="dash-greeting">Good day 👋</p>
          <h1 className="dash-title">{user?.name || 'Consultant'}</h1>
          <p className="dash-subtitle">Manage your clients and job postings from here.</p>
        </div>
        <div className="dash-header-right">
          <Link to="/consultant/jobs/create" className="dash-cta-btn">
            <span className="material-symbols-outlined">add</span>
            Create Job
          </Link>
        </div>
      </div>

      {/* Statistics */}
      <div className="dash-stats">
        <div className="dash-stat-card blue">
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">business</span>
            </div>
            <span className="dash-stat-trend up">
              <span className="material-symbols-outlined">arrow_upward</span>{stats.activeCompanies} active
            </span>
          </div>
          <div className="dash-stat-num">{stats.totalCompanies}</div>
          <div className="dash-stat-label">Companies</div>
        </div>

        <div className="dash-stat-card green">
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">work</span>
            </div>
            <span className="dash-stat-trend up">
              <span className="material-symbols-outlined">arrow_upward</span>{stats.activeJobs} active
            </span>
          </div>
          <div className="dash-stat-num">{stats.totalJobs}</div>
          <div className="dash-stat-label">Total Jobs</div>
        </div>

        <div className="dash-stat-card purple">
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">group</span>
            </div>
            {stats.pendingApplications > 0 && (
              <span className="dash-stat-alert">{stats.pendingApplications} new</span>
            )}
          </div>
          <div className="dash-stat-num">{stats.totalApplications}</div>
          <div className="dash-stat-label">Applications</div>
        </div>

        <div className="dash-stat-card orange">
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">draft</span>
            </div>
          </div>
          <div className="dash-stat-num">{stats.draftJobs}</div>
          <div className="dash-stat-label">Draft Jobs</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dash-section">
        <div className="dash-section-header">
          <h2>Quick Actions</h2>
        </div>
        <div className="dash-actions-grid">
          <Link to="/consultant/companies" className="dash-action-card accent-blue">
            <span className="material-symbols-outlined dash-action-icon-lg">business</span>
            <div>
              <h3>Manage Companies</h3>
              <p>View client companies</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/consultant/jobs" className="dash-action-card accent-green">
            <span className="material-symbols-outlined dash-action-icon-lg">work_history</span>
            <div>
              <h3>Manage Jobs</h3>
              <p>Edit job postings</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/consultant/jobs/create" className="dash-action-card accent-purple">
            <span className="material-symbols-outlined dash-action-icon-lg">post_add</span>
            <div>
              <h3>Create Job</h3>
              <p>Post a new job</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/consultant/applications" className="dash-action-card accent-orange">
            <span className="material-symbols-outlined dash-action-icon-lg">group</span>
            <div>
              <h3>Applications</h3>
              <p>Review candidates</p>
            </div>
            {stats.pendingApplications > 0 && (
              <span className="dash-notif-badge">{stats.pendingApplications}</span>
            )}
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      {/* <div className="dash-section">
        <div className="dash-section-header">
          <h2>Recent Activity</h2>
        </div>
        <div className="dash-recent-grid">

          <div className="dash-recent-card">
            <div className="dash-recent-header">
              <h3>Recent Companies</h3>
              <Link to="/consultant/companies" className="dash-section-link">View all</Link>
            </div>
            <div className="dash-recent-list">
              {recentCompanies.length === 0 ? (
                <div className="dash-empty-state">
                  <span className="material-symbols-outlined">business_center</span>
                  <p>No companies yet</p>
                </div>
              ) : recentCompanies.map((company) => (
                <div key={company._id || company.id} className="dash-recent-item">
                  <div className="dash-recent-item-avatar">
                    <span className="material-symbols-outlined">business</span>
                  </div>
                  <div className="dash-recent-item-info">
                    <h4>{company.name}</h4>
                    <p>Added {new Date(company.createdAt).toLocaleDateString()}</p>
                  </div>
                  {getStatusBadge(company.status)}
                </div>
              ))}
            </div>
          </div>


          <div className="dash-recent-card">
            <div className="dash-recent-header">
              <h3>Recent Jobs</h3>
              <Link to="/consultant/jobs" className="dash-section-link">View all</Link>
            </div>
            <div className="dash-recent-list">
              {recentJobs.length === 0 ? (
                <div className="dash-empty-state">
                  <span className="material-symbols-outlined">work_off</span>
                  <p>No jobs yet</p>
                </div>
              ) : recentJobs.map((job) => (
                <div key={job._id || job.id} className="dash-recent-item">
                  <div className="dash-recent-item-avatar">
                    <span className="material-symbols-outlined">work</span>
                  </div>
                  <div className="dash-recent-item-info">
                    <h4>{job.title}</h4>
                    <p>{job.company?.name || job.company} • {job.applicationsCount || job.applications || 0} applicants</p>
                  </div>
                  {getStatusBadge(job.status)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default ConsultantDashboard;
